<script lang="ts" setup>
import { useAdvisors } from '@/src/composables/useAdvisors';
import { useGlobalProperties } from '@/src/composables/useGlobalProperties';
import SeButton from '@/src/ui-kit/button/Button.vue';
import SeIcon from '@/src/ui-kit/icon/Icon.vue';
import SeOption from '@/src/ui-kit/select/Option.vue';
import SeSelect from '@/src/ui-kit/select/Select.vue';
import SeSpinner from '@/src/ui-kit/spinner/Spinner.vue';
import SeSwitch from '@/src/ui-kit/switch/Switch.vue';
import { theme } from '@/theme';
import { nextTick, onMounted, ref, watch } from 'vue';
import SeAdvisorCard from './advisor-card.vue';

type ListFilters = {
  query: string | undefined;
  categories: string[];
  onlineOnly: boolean;
  page: number;
  perPage: number;
  total: number;
  sort: string;
};

const defaultFilters: ListFilters = {
  query: undefined,
  categories: [],
  onlineOnly: true,
  sort: 'rankings_desc',
  page: 1,
  perPage: 10,
  total: 0,
};

const { advisor } = theme.home;

const { advisors, fetchAdvisors, page, perPage, total, nextPage, resetPage } = useAdvisors();
const { getGlobalProperties } = useGlobalProperties();
const { $auth, $route } = getGlobalProperties();

const filters = ref<ListFilters>(defaultFilters);
const loading = ref<boolean>(false);

const loadAdvisors = async () => {
  const [sort, order] = filters.value.sort.split('_');

  const { categories, onlineOnly, query } = filters.value;
  const searchQueryParams = {
    query,
    sort,
    order,
    limit: perPage.value,
    isAuthenticated: $auth?.check(),
    page: page.value,
    'categories[]': categories.length ? categories[0] : undefined,
    status: onlineOnly ? 'online' : undefined,
  };

  await fetchAdvisors(searchQueryParams);

  if ($route.query.type == 'more' && page.value == 1 && total.value > perPage.value) {
    loadMore();
  }

  nextTick(() => scrollToNextAdvisors());
};

const scrollToNextAdvisors = () => {
  if (page.value > 1) {
    const advisorsListContainer = document.getElementById('advisors-list-container');
    const childElements = advisorsListContainer?.children;
    const firstNewAddedElement = childElements?.[childElements.length - 6] as HTMLElement;
    if (firstNewAddedElement) {
      // Added some fallback value for header height
      const headerHeight = document.getElementsByTagName('header')[0].clientHeight || 100;
      window.scrollTo({
        top: firstNewAddedElement?.offsetTop - headerHeight,
        behavior: 'smooth',
      });
    }
  }
};

const loadMore = async () => {
  nextPage();
  await loadAdvisors();
};

watch(
  () => filters,
  () => {
    resetPage();
    loadAdvisors();
  },
  { deep: true }
);

onMounted(() => {
  if (props.onSearchCb && filters.value.query) {
    props.onSearchCb({
      query: filters.value.query,
      totalResults: total.value,
    });
  }
});

const props = withDefaults(
  defineProps<{
    query: Partial<ListFilters> & { search?: string };
    hasPaging: boolean;
    hasFilters?: boolean;
    onSearchCb?: (data: { query: string; totalResults: number }) => void;
  }>(),
  {
    hasFilters: true,
  }
);

filters.value = {
  ...defaultFilters,
  query: props.query.search,
};
</script>

<script lang="ts">
export default {
  name: 'SeAdvisorAdvisorsList',
};
</script>

<template>
  <div class="advisors-list" :class="advisor?.cardName">
    <div class="flex flex-col flex-wrap md:flex-row justify-between md:items-center md:pb-32">
      <div class="pr-24 pb-16">
        <slot name="title" :total="total" :loading="loading" />
      </div>
      <div v-if="hasFilters" class="flex items-center w-full md:w-auto pb-16">
        <div class="border border-neutral-30 rounded-8 h-40 md:h-48 flex items-center px-12 md:px-16 mr-12 md:mr-24">
          <div>
            <se-switch v-model="filters.onlineOnly">
              <p class="text-h-m font-semibold whitespace-nowrap">Online only</p>
            </se-switch>
          </div>
        </div>
        <se-select v-model="filters.sort" class="w-full max-w-272 !h-40 md:!h-48">
          <template #prefix>
            <se-icon name="arrow-down-up" :size="20" class="mr-12" />
          </template>
          <template #default>
            <se-option value="rankings_desc" label="Top-Rated" />
            <se-option value="sessions_desc" label="Most Sessions Held" />
            <se-option value="registered_desc" label="Newest" />
            <se-option value="price_asc" label="Price: Low to High" />
            <se-option value="price_desc" label="Price: High to Low" />
          </template>
        </se-select>
      </div>
    </div>
    <div
      v-if="hasFilters && filters.query?.length"
      class="flex items-center justify-between md:justify-start pb-24 md:pb-32 pt-8 md:pt-0 gap-16"
    >
      <p class="text-b-xl font-medium">Search: {{ filters.query }}</p>
      <se-button size="small" type="tertiary" @click="filters.query = ''"> Clear </se-button>
    </div>
    <div v-if="advisors.length > 0" class="advisors-list-container" id="advisors-list-container">
      <article :id="`advisor-card-${advisor.id}`" v-for="advisor of advisors" :key="advisor.id" class="w-full">
        <se-advisor-card :advisor="advisor" />
      </article>
    </div>
    <div v-else-if="loading" class="flex justify-center py-88">
      <se-spinner />
    </div>
    <div v-else>
      <h6 class="text-b-xl">There are no advisors matching your criteria</h6>
    </div>
    <div v-if="hasPaging" id="loadMoreButton" class="flex justify-center items-center gap-2 flex-col mt-28 pb-10">
      <p v-if="advisors.length" class="text-b-xs text-neutral-40">Loaded {{ advisors.length }} of {{ total }}</p>
      <se-button
        :disabled="advisors.length >= total"
        :loading="loading && advisors.length > 0"
        size="large"
        class="!px-28"
        @click="loadMore()"
      >
        Load more
      </se-button>
    </div>
  </div>
</template>
